import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { ConfiguratorInvite } from "@/configurator/invites/configuratorInvite";
import {
  inviteEmployeeLabel,
  inviteOldVehicleLabel,
  inviteRestrictionSummary,
  isInviteExpired,
} from "@/configurator/invites/configuratorInviteFields";
import {
  type ConfiguratorInviteFormValues,
  toConfiguratorInviteInput,
  toReplacementInviteFormValues,
} from "@/configurator/invites/configuratorInviteForm";
import { ConfiguratorInviteSheet } from "@/configurator/invites/ConfiguratorInviteSheet";
import { useCreateConfiguratorInvite } from "@/configurator/invites/useCreateConfiguratorInvite";
import { EmployeeCombobox } from "@/configurator/pickers/EmployeePicker";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorInvites } from "@/lib/api/generated/configurator/configurator";
import { useGetAdminContractExpirationsId } from "@/lib/api/generated/contract-expiration/contract-expiration";
import { formatDateOrEmpty } from "@rumbledirect/ui-v2/format";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_configurator_invite_create";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `configurator-invite-skeleton-${index}`,
);

interface InvitesSearch {
  readonly page: number;
  /** UUID of the driver the list is narrowed to, or `undefined` for all. */
  readonly employee?: string;
  /**
   * The expiring contract to open the invite form for, straight away.
   *
   * Letting a driver compose their own replacement is decided somewhere else -
   * on the expiration of their contract - and carried out here. Naming the
   * contract in the URL lets that other screen hand the work over whole: the
   * driver, the vehicle being replaced and the date it has to be gone are all
   * derivable from it, so the user does not retype what they just looked at.
   */
  readonly forContract?: string;
}

export const Route = createFileRoute("/_authenticated/configurator/invites")({
  validateSearch: (search: Record<string, unknown>): InvitesSearch => {
    const page = Number(search.page);
    const employee =
      typeof search.employee === "string" && search.employee !== ""
        ? search.employee
        : undefined;
    const forContract =
      typeof search.forContract === "string" && search.forContract !== ""
        ? search.forContract
        : undefined;

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      ...(employee ? { employee } : {}),
      ...(forContract ? { forContract } : {}),
    };
  },
  component: ConfiguratorInvitesPage,
});

function ConfiguratorInvitesPage(): ReactNode {
  const { page, employee, forContract } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminConfiguratorInvites(
      { page, ...(employee ? { employee } : {}) },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<
    ConfiguratorInviteFormValues | undefined
  >(undefined);

  // The expiration the form is to be seeded from, when the page was opened to
  // settle one. Fetched rather than passed in pieces through the URL, so the
  // form is seeded from the contract as it stands and not from a link that was
  // built at some earlier point.
  const { data: expiration } = useGetAdminContractExpirationsId(
    forContract ?? "",
    { query: { enabled: forContract !== undefined } },
  );

  useEffect(() => {
    if (expiration === undefined) {
      return;
    }

    setCreateDefaults(toReplacementInviteFormValues(expiration));
    setCreateOpen(true);

    // Spent the moment the form is up: a reload or a second visit to the list
    // should show the invitations, not the form again.
    void navigate({
      search: (prev) => ({ ...prev, forContract: undefined }),
      replace: true,
    });
  }, [expiration, navigate]);

  const createMutation = useCreateConfiguratorInvite();

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button
            size="sm"
            onClick={() => {
              // A plain new invitation, even if the last one was opened to
              // settle an expiration.
              setCreateDefaults(undefined);
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Nieuwe uitnodiging
          </Button>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Uitnodigingen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Nodig medewerkers uit om hun volgende voertuig samen te stellen en
          bepaal waaruit zij mogen kiezen.
        </p>
      </div>

      <EmployeeFilter
        employee={employee}
        onChange={(next) =>
          // Back to page 1: the current page number is meaningless against a
          // different result set.
          void navigate({
            search: () => ({ page: 1, ...(next ? { employee: next } : {}) }),
          })
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Medewerker</Th>
              <Th className="w-36">Uitgenodigd op</Th>
              <Th className="w-40">Geldig tot</Th>
              <Th className="w-44">Keuze</Th>
              <Th className="w-40">Vervangt</Th>
              <Th className="w-24 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ConfiguratorInviteRows
              isPending={isPending}
              isError={isError}
              members={members}
              isFiltered={employee !== undefined}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      {/* Mounted only while open: the sheet loads drivers, categories and
          conversions, which is wasted work on a page that is mostly read. */}
      {createOpen && (
        <ConfiguratorInviteSheet
          open
          onOpenChange={setCreateOpen}
          defaults={createDefaults}
          isSubmitting={createMutation.isPending}
          onSubmit={(values) => {
            createMutation.mutate(toConfiguratorInviteInput(values), {
              onSuccess: () => setCreateOpen(false),
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * Narrows the list to one driver. The collection can only be filtered by
 * driver, which is also how the overview is actually used: "what did we send
 * this person?".
 */
function EmployeeFilter({
  employee,
  onChange,
}: {
  readonly employee: string | undefined;
  readonly onChange: (employee: string | undefined) => void;
}): ReactNode {
  return (
    <div className="max-w-sm">
      <EmployeeCombobox
        value={employee ?? ""}
        onChange={(value) => onChange(value || undefined)}
        filter
      />
    </div>
  );
}

function ConfiguratorInviteRows({
  isPending,
  isError,
  members,
  isFiltered,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly ConfiguratorInvite[] | undefined;
  readonly isFiltered: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-28" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <div className="flex justify-end">
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={6} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de uitnodigingen niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={6} className="py-10 text-center text-muted-foreground">
          {isFiltered
            ? "Deze medewerker heeft nog geen uitnodiging."
            : "Nog geen uitnodigingen."}
        </Td>
      </Tr>
    );
  }

  return members.map((invite) => (
    <Tr key={invite.id}>
      <Td className="font-medium">
        <InviteDetailLink invite={invite}>
          {inviteEmployeeLabel(invite)}
        </InviteDetailLink>
      </Td>
      <Td>{formatDateOrEmpty(invite.createdAt)}</Td>
      <Td>
        <div className="flex items-center gap-2">
          {formatDateOrEmpty(invite.expiresAt)}
          {isInviteExpired(invite) && (
            <Badge variant="neutral" size="sm">
              Verlopen
            </Badge>
          )}
        </div>
      </Td>
      <Td className="text-muted-foreground">
        {inviteRestrictionSummary(invite)}
      </Td>
      <Td className="text-muted-foreground">{inviteOldVehicleLabel(invite)}</Td>
      <Td>
        <div className="flex justify-end">
          <InviteDetailLink
            invite={invite}
            aria-label={`Uitnodiging voor ${inviteEmployeeLabel(invite)} openen`}
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </InviteDetailLink>
        </div>
      </Td>
    </Tr>
  ));
}

/**
 * Links a row to the invite's detail page, where editing, withdrawing and the
 * link into the my app live. Renders plain text for an invite without an id,
 * which the API should not return but the generated type allows.
 */
function InviteDetailLink({
  invite,
  children,
  className,
  ...rest
}: {
  readonly invite: ConfiguratorInvite;
  readonly children: ReactNode;
  readonly className?: string;
  readonly "aria-label"?: string;
}): ReactNode {
  if (!invite.id) return children;

  return (
    <Link
      {...rest}
      to="/configurator/invites/$configuratorInviteId"
      params={{ configuratorInviteId: invite.id }}
      className={
        className ??
        "rounded-sm transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      }
    >
      {children}
    </Link>
  );
}
